import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Github, Twitter, Rss, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [footerEmail, setFooterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [pricingEnabled, setPricingEnabled] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setPricingEnabled(res.data.pricing_enabled !== false);
      } catch { /* use default */ }
    };
    fetchSettings();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!footerEmail.trim()) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: footerEmail });
      toast.success('Subscribed successfully!');
      setFooterEmail('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to subscribe'); }
    setSubscribing(false);
  };

  return (
    <footer className="bg-slate-900 border-t border-slate-800" data-testid="footer">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img 
                src="/vm2pod-logo.png" 
                alt="vm2pod logo" 
                className="h-10 w-auto object-contain"
              />
              <span className="font-heading font-bold text-xl text-white">
                vm<span className="text-blue-400">2</span>pod
              </span>
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed">
              From VMs to Pods — bridging legacy infrastructure to modern cloud-native architectures.
            </p>
            <div className="flex items-center gap-4 mt-6">
              {[Twitter, Github, Rss, Mail].map((Icon, i) => (
                <a key={i} href="#" className="text-slate-500 hover:text-blue-400 transition-colors"><Icon className="w-5 h-5" /></a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">Explore</h4>
            <ul className="space-y-3">
              {['Articles', 'Authors', ...(pricingEnabled ? ['Pricing'] : [])].map(item => (
                <li key={item}>
                  <Link to={item === 'Articles' ? '/' : `/${item.toLowerCase()}`} className="text-sm text-slate-500 hover:text-blue-400 transition-colors">{item}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">Topics</h4>
            <ul className="space-y-3">
              {['VMware', 'Kubernetes', 'DevOps', 'Cloud Native', 'Networking'].map(item => (
                <li key={item}>
                  <Link to={`/category/${item.toLowerCase().replace(' ', '-')}`} className="text-sm text-slate-500 hover:text-blue-400 transition-colors">{item}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">Stay Updated</h4>
            <p className="text-sm text-slate-500 mb-4">Weekly infrastructure insights delivered to your inbox.</p>
            <form onSubmit={handleSubscribe} className="flex gap-2" data-testid="footer-newsletter-form">
              <Input type="email" placeholder="your@email.com" value={footerEmail} onChange={(e) => setFooterEmail(e.target.value)}
                className="h-9 text-sm bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-violet-500 focus:ring-violet-500/20" required data-testid="footer-newsletter-email" />
              <Button type="submit" size="sm" disabled={subscribing} className="animated-gradient-bg text-white shrink-0 btn-press" data-testid="footer-newsletter-btn">
                <ArrowRight className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>

        <div className="relative z-10 border-t border-slate-800 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} vm2pod.com. All rights reserved.</p>
          <p className="text-xs text-slate-600">Built for the infrastructure community</p>
        </div>
      </div>
    </footer>
  );
}
